<vs-app-page
  *vsLet="filters$ | async as filters"
  [config]="{
    title: 'Warranties',
    icon: 'folder-shield',
    showDivider: true,
    description: 'This is an overview of all warranties filed across your organization.',
  }"
>
  <vs-breadcrumbs slot="breadcrumbs" [config]="{ items: [{ label: 'Warranties' }] }"></vs-breadcrumbs>

  <vs-badge
    *vsLet="totalWarranties$ | async as totalWarranties"
    slot="title-end"
    [class.italic]="totalWarranties === null"
    [class.animate-pulse]="totalWarranties === null"
  >
    {{
      totalWarranties === null
        ? 'loading'
        : (totalWarranties | number) + ' ' + (totalWarranties === 1 ? 'Warranty' : 'Warranties')
    }}
  </vs-badge>

  <!-- Buttons -->
  <div *ngIf="warrantyCount !== 0" class="flex w-full sm:max-w-min gap-4" slot="header-end">
    <button
      *ngIf="canSellWarranty$ | async"
      vs-button
      size="md"
      theme="primary"
      class="flex-grow"
      (click)="openNewWarranty()"
    >
      <vs-icon name="folder-plus"></vs-icon>
      New Warranty
    </button>
  </div>

  <form [formGroup]="form">
    <div class="flex gap-2 justify-between items-center flex-wrap mt-4">
      <div class="ml-auto flex items-start gap-2 py-1.5 sm:py-0 w-full sm:w-auto">
        <button
          #filtersTrigger="cdkOverlayOrigin"
          cdkOverlayOrigin
          vs-button
          appearance="outline"
          type="button"
          (click)="$event.preventDefault(); $event.stopImmediatePropagation(); toggleFiltersOverlay()"
        >
          <vs-icon name="sliders-04" slot="start"></vs-icon>
          Filters
          <vs-badge *ngIf="activeFilterCount > 0" class="ml-1" theme="default">{{ activeFilterCount }}</vs-badge>
        </button>

        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="filtersTrigger"
          [cdkConnectedOverlayOpen]="isFiltersOverlayOpen"
          [cdkConnectedOverlayPositions]="positions"
          (overlayOutsideClick)="closeFiltersOverlay()"
          (detach)="closeFiltersOverlay()"
        >
          <vs-card class="my-2 !rounded-md !shadow-md" [style.maxWidth]="'min(90vw, 576px)'">
            <div class="p-3 w-[min(90vw,576px)] max-h-[70vh] overflow-y-auto" (click)="$event.stopPropagation()">
              <div class="flex items-center justify-between mb-2">
                <p class="text-sm font-semibold text-themed-900">Filters</p>
                <button vs-button appearance="clear" size="sm" type="button" (click)="clearAllFilters()">
                  Clear all
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div class="flex flex-col gap-3">
                  <ws-customer-picker formControlName="selectedCustomer"></ws-customer-picker>
                  <ws-policy-filter-picker formControlName="policyIds"></ws-policy-filter-picker>
                </div>

                <div class="flex flex-col gap-3">
                  <ws-warranty-status-picker formControlName="status"></ws-warranty-status-picker>

                  <button
                    type="button"
                    class="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white w-full hover:border-gray-400 transition-colors"
                    [vsMenuTriggerFor]="dateRangeMenu"
                  >
                    <vs-icon name="calendar" class="text-gray-400" size="16"></vs-icon>
                    <div class="flex-1 text-left text-sm">
                      <ng-container
                        *ngIf="form.controls.startDate.value || form.controls.endDate.value; else noDateRange"
                      >
                        <span class="text-gray-900">
                          {{
                            form.controls.startDate.value ? (form.controls.startDate.value | date: 'MMM d') : 'Start'
                          }}
                          -
                          {{ form.controls.endDate.value ? (form.controls.endDate.value | date: 'MMM d, y') : 'End' }}
                        </span>
                      </ng-container>
                      <ng-template #noDateRange>
                        <span class="text-gray-500">Select date range</span>
                      </ng-template>
                    </div>
                    <vs-icon name="chevron-down" class="text-gray-400" size="14"></vs-icon>
                  </button>

                  <ng-template #dateRangeMenu>
                    <vs-menu class="!z-50">
                      <div class="p-4">
                        <vs-date-picker [range]="true" (selectionChanged)="onDateRangeChange($event)"></vs-date-picker>
                      </div>
                    </vs-menu>
                  </ng-template>
                </div>
              </div>
            </div>
          </vs-card>
        </ng-template>

        <vs-input-container class="min-w-[200px] sm:max-w-[212px] w-full sm:w-auto ml-auto">
          <vs-icon name="search-lg" slot="start"></vs-icon>
          <input type="text" placeholder="Search warranties..." formControlName="search" />
        </vs-input-container>
      </div>
    </div>
  </form>

  <ng-container
    *vsLet="{
      warranties: warranties$ | async,
      paginationConfig: paginationConfig$ | async,
      loading: loading$ | async,
      totalWarranties: totalWarranties$ | async,
    } as data"
  >
    <!-- Table -->
    <ng-container *ngIf="data?.loading || warrantyCount !== 0; else noWarranties">
      <div class="rounded-xl overflow-hidden border border-gray-200 shadow-sm mt-4">
        <ng-container *ngIf="data">
          <!-- Skeleton Loader (show while loading OR total is still null) -->
          <vs-table-skeleton
            *ngIf="data.loading || data.totalWarranties === null"
            [rows]="data?.paginationConfig?.itemsPerPage ?? 10"
            [columns]="7"
            [columnDefs]="[
              {},
              { fitContent: true },
              {},
              { fitContent: true },
              { fitContent: true },
              { fitContent: true },
              { fitContent: true },
            ]"
            [showHeader]="true"
            [banding]="true"
          ></vs-table-skeleton>

          <!-- Show table when we have data -->
          <ng-container *ngIf="!data.loading && data.totalWarranties !== null">
            <vs-table
              [columns]="columns"
              [data]="data.warranties"
              (rowClicked)="rowClicked($event)"
              (sortChanged)="onSortChanged($event)"
            ></vs-table>
            <vs-pagination [config]="data.paginationConfig" (pagination)="pagination$.next($event)"></vs-pagination>
          </ng-container>
        </ng-container>
      </div>
    </ng-container>

    <!-- Empty state -->
    <ng-template #noWarranties>
      <vs-card *ngIf="warrantyCount === 0" class="flex flex-col justify-center gap-2 p-6 mt-4">
        <vs-empty-state
          [config]="{
            title:
              filters?.customerId || filters?.search || filters?.startDate || filters?.endDate
                ? 'No warranties found with that search query.'
                : filters?.status?.length
                  ? 'No ' + (filters?.status?.join(', ') | lowercase) + ' warranties found.'
                  : filters?.policyIds
                    ? 'No warranties found under the selected policies.'
                    : 'No warranties found.',
            subtitle:
              filters?.customerId || filters?.search || filters?.startDate || filters?.endDate
                ? 'Adjust the filters to view warranties for your organization.'
                : filters?.status?.length
                  ? 'Once a warranty has been ' +
                    (filters?.status?.includes(activated) ? 'purchased' : 'set to one of the selected statuses') +
                    ', its details will appear here.'
                  : filters?.policyIds
                    ? 'Once a warranty has been purchased under selected policies their details will appear here.'
                    : 'Once a warranty has been purchased, its details will appear here.',

            imgsrc: 'empty-clipboard',
          }"
        ></vs-empty-state>
        <button
          *ngIf="canSellWarranty$ | async"
          size="md"
          vs-button
          class="self-center"
          theme="primary"
          (click)="openNewWarranty()"
        >
          <vs-icon name="folder-plus"></vs-icon>
          New Warranty
        </button>
      </vs-card>
    </ng-template>
  </ng-container>
</vs-app-page>
